import React, { useState, useEffect } from 'react';
import Modal from '../../../components/ui/Modal';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const ContractsActionsModal = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('actions');
  const [formData, setFormData] = useState({
    contractType: 'rental',
    partyA: '',
    partyB: '',
    propertyId: '',
    duration: '',
    amount: '',
    terms: '',
    aiReview: false,
    legalCheck: false
  });

  // Listen for e-signature modal open event
  useEffect(() => {
    const handleOpenESignature = () => {
      onClose();
      setTimeout(() => {
        const event = new CustomEvent('openESignatureModal');
        window.dispatchEvent(event);
      }, 100);
    };

    window.addEventListener('openESignatureFromContracts', handleOpenESignature);
    return () => window.removeEventListener('openESignatureFromContracts', handleOpenESignature);
  }, [onClose]);

  const actions = [
    { id: 'ai-generate', icon: 'Wand2', label: 'إنشاء عقد ذكي بالذكاء الاصطناعي', color: '#F59E0B' },
    { id: 'legal-review', icon: 'Shield', label: 'مراجعة قانونية', color: '#EF4444' },
    { id: 'auto-fill', icon: 'Wand2', label: 'تعبئة تلقائية', color: '#8B5CF6' },
    { id: 'clause-suggest', icon: 'List', label: 'اقتراح بنود', color: '#3B82F6' },
    { id: 'version-compare', icon: 'GitCompare', label: 'مقارنة الإصدارات', color: '#10B981' },
    { id: 'e-signature', icon: 'PenTool', label: 'توقيع إلكتروني', color: '#06B6D4' },
    { id: 'track-modifications', icon: 'Activity', label: 'تتبع التعديلات والتوقيعات', color: '#EC4899' }
  ];

  const templates = [
    { id: 'rental-residential', name: 'عقد إيجار سكني', clauses: 12 },
    { id: 'rental-commercial', name: 'عقد إيجار تجاري', clauses: 15 },
    { id: 'sale', name: 'عقد بيع', clauses: 18 },
    { id: 'partnership', name: 'عقد شراكة', clauses: 20 },
    { id: 'management', name: 'عقد إدارة', clauses: 14 }
  ];

  const handleAction = (actionId) => {
    if (actionId === 'ai-generate') {
      onClose();
      setTimeout(() => {
        const event = new CustomEvent('openAIContractGenerator');
        window.dispatchEvent(event);
      }, 100);
    } else if (actionId === 'e-signature') {
      onClose();
      setTimeout(() => {
        const event = new CustomEvent('openESignatureModal');
        window.dispatchEvent(event);
      }, 100);
    } else if (actionId === 'track-modifications') {
      onClose();
      setTimeout(() => {
        // For demo, use test contract ID from migration
        const event = new CustomEvent('openContractModificationTracker', {
          detail: { contractId: 'demo-contract-001' }
        });
        window.dispatchEvent(event);
      }, 100);
    } else {
      console.log('Contract action:', actionId);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Modal 
      isOpen={isOpen} 
      onClose={onClose} 
      title="قوالب العقود - الإجراءات والقوالب"
      icon={null}
      iconColor={null}
      footer={null}
    >
      <div className="space-y-6">
        <div className="flex gap-2 border-b border-border">
          <button
            onClick={() => setActiveTab('actions')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'actions' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            الإجراءات
          </button>
          <button
            onClick={() => setActiveTab('templates')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'templates' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            القوالب
          </button>
          <button
            onClick={() => setActiveTab('fields')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'fields' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            الحقول
          </button>
        </div>

        {activeTab === 'actions' && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              {actions?.map((action) => (
                <button
                  key={action?.id}
                  onClick={() => handleAction(action?.id)}
                  className="p-4 rounded-lg border border-border hover:border-primary transition-all text-right group"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${action?.color}20` }}
                    >
                      <Icon name={action?.icon} size={20} color={action?.color} />
                    </div>
                    <span className="text-sm font-medium text-foreground group-hover:text-primary">
                      {action?.label}
                    </span>
                  </div>
                </button>
              ))}
            </div>

            <div className="bg-muted/50 rounded-lg p-4 space-y-3">
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <Icon name="Shield" size={16} />
                خيارات المراجعة القانونية
              </h3>
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData?.aiReview}
                    onChange={(e) => handleInputChange('aiReview', e?.target?.checked)}
                    className="w-4 h-4 rounded border-border"
                  />
                  <span className="text-sm text-foreground">مراجعة ذكية للبنود</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData?.legalCheck}
                    onChange={(e) => handleInputChange('legalCheck', e?.target?.checked)}
                    className="w-4 h-4 rounded border-border"
                  />
                  <span className="text-sm text-foreground">فحص الامتثال القانوني</span>
                </label>
              </div>
            </div>

            <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Icon name="Info" size={20} color="#3B82F6" />
                <div>
                  <h4 className="text-sm font-semibold text-foreground">ملاحظة قانونية</h4>
                  <p className="text-xs text-muted-foreground mt-1">
                    جميع العقود المُنشأة يجب مراجعتها من قبل مستشار قانوني قبل التوقيع
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'templates' && (
          <div className="space-y-3">
            {templates?.map((template) => (
              <div
                key={template?.id}
                className="p-4 rounded-lg border border-border hover:border-primary transition-all cursor-pointer"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-sm font-semibold text-foreground">{template?.name}</h4>
                    <p className="text-xs text-muted-foreground mt-1">
                      {template?.clauses} بند قانوني
                    </p>
                  </div>
                  <Button variant="outline" size="sm">
                    استخدام
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'fields' && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">نوع العقد</label>
                <select
                  value={formData?.contractType}
                  onChange={(e) => handleInputChange('contractType', e?.target?.value)}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground"
                >
                  <option value="rental">إيجار</option>
                  <option value="sale">بيع</option>
                  <option value="partnership">شراكة</option>
                  <option value="management">إدارة</option>
                </select>
              </div>
              <Input
                label="الطرف الأول"
                value={formData?.partyA}
                onChange={(e) => handleInputChange('partyA', e?.target?.value)}
                placeholder="اسم الطرف الأول"
              />
              <Input
                label="الطرف الثاني"
                value={formData?.partyB}
                onChange={(e) => handleInputChange('partyB', e?.target?.value)}
                placeholder="اسم الطرف الثاني"
              />
              <Input
                label="رقم العقار"
                value={formData?.propertyId}
                onChange={(e) => handleInputChange('propertyId', e?.target?.value)}
                placeholder="معرف العقار"
              />
              <Input
                label="المدة (شهور)"
                type="number"
                value={formData?.duration}
                onChange={(e) => handleInputChange('duration', e?.target?.value)}
                placeholder="12"
              />
              <Input
                label="المبلغ"
                type="number"
                value={formData?.amount}
                onChange={(e) => handleInputChange('amount', e?.target?.value)}
                placeholder="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">شروط إضافية</label>
              <textarea
                value={formData?.terms}
                onChange={(e) => handleInputChange('terms', e?.target?.value)}
                placeholder="أي شروط أو بنود إضافية..."
                rows={4}
                className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground resize-none"
              />
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4 border-t border-border">
          <Button variant="outline" onClick={onClose}>
            إلغاء
          </Button>
          <Button variant="default">
            إنشاء العقد
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ContractsActionsModal;